import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useMutation } from 'providers/tanstack-query/useMutation'
import { base64ToBuffer, downloadFile, XLSX_MIME_TYPE } from 'utils/file'
import { DOWNLOAD_AD_UNITS_CONFIGURATION } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseDownloadAdUnitsConfiguration, UseDownloadAdUnitsConfigurationProps } from './types'

export const useDownloadAdUnitsConfiguration = ({
  scope,
  workspaceName,
}: UseDownloadAdUnitsConfigurationProps): UseDownloadAdUnitsConfiguration => {
  const { t } = useTranslation('features/globalSetup')
  const { enqueueSnackbar } = useSnackbar()

  const FILENAME: string = `${workspaceName} - Ad units configuration.xlsx`

  const { isPending: loading, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: DOWNLOAD_AD_UNITS_CONFIGURATION,
    onCompleted: async (data: Data) => {
      const {
        downloadAdUnitsXls: { errors, fileData },
      } = data

      if (errors && errors.length > 0) {
        enqueueSnackbar(
          t('general.workspace.form.bulkManagement.download.error', { workspaceName }),
          {
            variant: 'error',
          },
        )
      } else if (fileData) {
        const decodedBase64: string = atob(fileData)
        const buffer: ArrayBuffer = base64ToBuffer(decodedBase64)
        const blob = new Blob([buffer], { type: XLSX_MIME_TYPE })

        downloadFile(blob, FILENAME)
      }
    },
  })

  const download: UseDownloadAdUnitsConfiguration['download'] = workspaceId =>
    void mutateAsync({
      input: {
        spreadsheetType: scope,
        workspaceId,
      },
    })

  return { download, loading }
}
